import type { FC } from 'react';
import type { IEvaultApi } from './use-evault-api';
import type {
	IRetentionCatalogue,
	IRetentionSchedule,
	IVault,
	IVaultCreated,
	IVaultDetail,
} from './types';

import { useCallback, useEffect, useState } from 'react';

import { Field, Modal } from 'views/design-system';

import { EVAULT_API } from './use-evault-api';

/** The server's own cap (`vaults/validator.ts` VAULT_NAME_MAX). */
export const VAULT_NAME_MAX = 60;

const REQUIRED = 'Vault name is required.';

/**
 * A vault that already has a schedule keeps it on every record filed so far. The
 * form says so rather than letting a change look retroactive.
 */
const scheduleChangedNote = (
	vault: IVault | IVaultDetail | null | undefined,
	scheduleCode: string
) =>
	scheduleCode && scheduleCode !== vault?.retentionPolicy?.scheduleCode ? (
		<div className="note">
			Applies to documents deposited from now on. Records already filed under{' '}
			{vault?.retentionPolicy?.scheduleCode} keep that schedule.
		</div>
	) : null;

/**
 * Create a vault (§8a) and rename one (§8b) — one component, because they are
 * one form.
 *
 * Rename differs from create in three strings and one verb; splitting it into a
 * second file would mean two copies of the reset-on-reopen rule, two copies of
 * the outcome mapping, and two places for them to drift. The distinction that
 * matters is in `mode`, and it is small enough to read here.
 *
 * WHAT THE ERRORS DO, AND WHY THEY ARE NOT TOASTS. A name clash is a correction
 * to make in the field that caused it, so the modal stays open with the typed
 * name intact and prints the server's own sentence under the input — retyping a
 * name from memory after a toast dismissed itself is the failure this avoids.
 * The one outcome that does close the modal is `unentitled`: that is not an
 * error in the form, it is a plan the workspace does not have, and it belongs in
 * the upsell (R10) rather than under a text field.
 */
export interface ICreateVaultModalProps {
	api: IEvaultApi;
	open: boolean;
	mode: 'create' | 'rename';
	/** The vault being renamed. Ignored in `create` mode. */
	vault?: IVault | IVaultDetail | null;
	onClose: () => void;
	/** `create` succeeded. Carries `isFirstVault`, which decides the next screen. */
	onCreated?: (vault: IVaultCreated) => void;
	/** `rename` succeeded. The server answers with the whole vault. */
	onRenamed?: (vault: IVaultDetail) => void;
	/** 403 EVAULT_NOT_ENTITLED — close and show the add-on instead. */
	onUnentitled?: () => void;
}

export const CreateVaultModal: FC<ICreateVaultModalProps> = ({
	api,
	open,
	mode,
	vault,
	onClose,
	onCreated,
	onRenamed,
	onUnentitled,
}) => {
	const [name, setName] = useState('');
	const [error, setError] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	/* §10 — the record category. '' means "no schedule", which the server accepts
	   and reports honestly as NOT AVAILABLE on every certificate from this vault. */
	const [scheduleCode, setScheduleCode] = useState('');
	const [catalogue, setCatalogue] = useState<IRetentionSchedule[]>([]);
	/* Rename mode, vault previously unscheduled: offer to stamp the records already
	   in it. Off by default — applying a schedule to existing records is a decision. */
	const [applyToExisting, setApplyToExisting] = useState(false);

	const isRename = mode === 'rename';
	const hadPolicy = Boolean(vault?.retentionPolicy);

	/* Fetched once per open, not per keystroke. The catalogue is static; the token
	   is the only reason it is a request at all. */
	useEffect(() => {
		if (!open) return;
		let cancelled = false;
		void api.get<IRetentionCatalogue>(EVAULT_API.VAULT_RETENTION_SCHEDULES).then(r => {
			if (!cancelled && r.kind === 'ok') setCatalogue(r.data.schedules);
		});
		return () => {
			cancelled = true;
		};
	}, [api, open]);

	/* A reopened modal must not inherit the previous attempt's name or error —
	   the behaviour the component upstream had, kept because the alternative is
	   a "create" dialog that opens pre-filled with the last clash. Rename opens
	   on the current name so the common edit is a word, not a retype. */
	useEffect(() => {
		if (!open) return;
		setName(isRename ? vault?.name ?? '' : '');
		setScheduleCode(isRename ? vault?.retentionPolicy?.scheduleCode ?? '' : '');
		setApplyToExisting(false);
		setError('');
		setIsSubmitting(false);
		// The vault object identity changes on every list refetch; its NAME is
		// what this form seeds from.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, isRename, vault?.name]);

	const change = useCallback((next: string) => {
		setName(next);
		setError('');
	}, []);

	/* Closing mid-flight would leave a request writing into an unmounted form
	   and, worse, hide the outcome of a create that is going to succeed. */
	const close = useCallback(() => {
		if (isSubmitting) return;
		onClose();
	}, [isSubmitting, onClose]);

	const submit = useCallback(async () => {
		const trimmed = name.trim();
		if (!trimmed) {
			setError(REQUIRED);
			return;
		}

		setIsSubmitting(true);
		/* Branched rather than a ternary on the awaited value: the two calls
		   answer with different shapes (`isFirstVault` on one, `breadcrumb` on
		   the other), and a union of them would have to be cast apart again at
		   the call sites that care about exactly those fields. */
		/* The schedule is sent only when it CHANGED: a PATCH that re-sends the same
		   code would write a fresh "set by / set at" over the original decision, and
		   the approver on record should stay the person who actually decided. */
		const scheduleChanged =
			scheduleCode !== (vault?.retentionPolicy?.scheduleCode ?? '');
		const retention = scheduleCode ? { scheduleCode } : undefined;

		let result;
		if (isRename) {
			/* Only what changed. Re-sending an unchanged name would write a
			   `vault_renamed` audit row for a rename that did not happen. */
			const nameChanged = trimmed !== (vault?.name ?? '');
			if (!nameChanged && !(scheduleChanged && retention)) {
				setIsSubmitting(false);
				onClose();
				return;
			}
			const renamed = await api.patch<IVaultDetail>(
				`${EVAULT_API.VAULTS}/${vault?._id}`,
				{
					...(nameChanged ? { name: trimmed } : {}),
					...(scheduleChanged && retention
						? { retention, applyToExisting: !hadPolicy && applyToExisting }
						: {}),
				}
			);
			if (renamed.kind === 'ok') {
				setIsSubmitting(false);
				onRenamed?.(renamed.data);
				return;
			}
			result = renamed;
		} else {
			const madeVault = await api.post<IVaultCreated>(EVAULT_API.VAULTS, {
				name: trimmed,
				...(retention ? { retention } : {}),
			});
			if (madeVault.kind === 'ok') {
				setIsSubmitting(false);
				onCreated?.(madeVault.data);
				return;
			}
			result = madeVault;
		}
		setIsSubmitting(false);

		if (result.kind === 'unentitled') {
			onClose();
			onUnentitled?.();
			return;
		}

		/* Every remaining failure is shown verbatim. The 409 is the one worth
		   naming — "A vault with this name already exists" — but a 400 from the
		   validator ("Vault name must be 60 characters or fewer") is equally
		   specific, and a generic fallback would replace a usable instruction
		   with "something went wrong". */
		setError(result.message);
	}, [
		api,
		applyToExisting,
		hadPolicy,
		isRename,
		name,
		onClose,
		onCreated,
		onRenamed,
		onUnentitled,
		scheduleCode,
		vault,
	]);

	const chosen = catalogue.find(s => s.code === scheduleCode) ?? null;

	return (
		<Modal
			open={open}
			title={isRename ? 'Rename vault' : 'Create a new vault'}
			size="narrow"
			className="evault-modal"
			onClose={close}
			footer={
				<>
					<button
						className="dbtn"
						type="button"
						disabled={isSubmitting}
						onClick={close}
					>
						Cancel
					</button>
					<button
						className="dbtn pri"
						type="button"
						disabled={isSubmitting || !name.trim()}
						onClick={() => void submit()}
					>
						{isRename ? 'Save' : 'Create vault'}
					</button>
				</>
			}
		>
			<Field
				label="Vault name"
				value={name}
				onChange={change}
				error={error}
				max={VAULT_NAME_MAX}
				placeholder="e.g. Q2 Sales Contracts"
				autoFocus
			/>

			{/* §10 — the record category decides the retention schedule every document
			    deposited here is filed under: the governing rule, the period, and when
			    the clock starts. Choosing one is the approval the certificate cites;
			    leaving it blank is allowed and reported as NOT AVAILABLE. */}
			<div className="fld">
				<div className="flab">Record category (retention schedule)</div>
				<select
					aria-label="Record category"
					value={scheduleCode}
					disabled={isSubmitting}
					onChange={e => setScheduleCode(e.target.value)}
				>
					<option value="">No schedule assigned</option>
					{catalogue.map(s => (
						<option key={s.code} value={s.code}>
							{s.category} —{' '}
							{s.retentionYears === null ? 'permanent' : `${s.retentionYears} years`}
						</option>
					))}
				</select>
				{chosen ? (
					<div className="fhint">
						{chosen.governingRule} · {chosen.triggerLabel.toLowerCase()} ·{' '}
						{chosen.dispositionAuthority}
						{chosen.accessibleYears !== null
							? ` · first ${chosen.accessibleYears} years easily accessible`
							: ''}
					</div>
				) : (
					<div className="fhint">
						Without a schedule, certificates from this vault report retention as
						NOT AVAILABLE.
					</div>
				)}
			</div>

			{isRename && !hadPolicy && scheduleCode ? (
				<label className="fld" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
					<input
						type="checkbox"
						style={{ width: 'auto' }}
						checked={applyToExisting}
						disabled={isSubmitting}
						onChange={e => setApplyToExisting(e.target.checked)}
					/>
					<span>
						Also file the {vault?.documentCount ?? 0} document
						{(vault?.documentCount ?? 0) === 1 ? '' : 's'} already in this vault
						under this schedule
					</span>
				</label>
			) : null}

			{isRename && hadPolicy && scheduleChangedNote(vault, scheduleCode)}

			{!isRename && (
				<div className="note">
					Documents stored here are encrypted at rest and linked to this Simplici
					workspace only.
				</div>
			)}
		</Modal>
	);
};

export default CreateVaultModal;
